import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { paginationParams } from '@app/shared/config/table.config';
import { columnDefs } from './vendor-list-rto-drill-config';
import {
  ErrorMessages,
  Attendance_Items,
} from '@app/shared/constants/constants';
import { SearchService } from '@app/shared/services/search.service';
import { RTODrillData } from '@app/shared/utils/utils';
import {
  EmeraldButtonSize,
  EmeraldButtonVariant,
  EmeraldButtonAlignment,
  CalendarDay,
  PersonaType,
} from '@constants/global.enum';
import { RTODrill, RtoTypeEnum } from '@app/shared/constants/global.enum';
import { VendorListRtoDrillService } from './vendor-list-rto-drill.service';
import { RTOdrill, RTOdrillResponse } from '@app/shared/interfaces/rto-drill';
import { ToastrService } from 'ngx-toastr';
import { ActivatedRoute, Router } from '@angular/router';

@Component({
  selector: 'app-vendor-list-rto-drill',
  templateUrl: './vendor-list-rto-drill.component.html',
  styleUrl: './vendor-list-rto-drill.component.scss',
})
export class VendorListRtoDrillComponent implements OnInit, OnDestroy {
  readonly calendarLegend = Attendance_Items;
  get selectedYear(): number {
    return this.year;
  }

  get selectedMonth(): any {
    return this.month;
  }

  private readonly destroy$ = new Subject<void>();

  readonly EmeraldButtonSize = EmeraldButtonSize;
  readonly EmeraldButtonVariant = EmeraldButtonVariant;
  readonly EmeraldButtonAlignment = EmeraldButtonAlignment;

  readonly paginationParams = paginationParams;
  columnDefs = columnDefs;
  readonly iconFontSize = RTODrillData.TABLE_CONFIG.ICON_FONT_SIZE;
  readonly rtoData = RTODrillData;
  readonly dayHeaders = [...RTODrillData.CALENDAR.DAY_HEADERS];

  calendarDays: CalendarDay[] = [];
  selectedContractor: any = null;
  isDataLoaded = true;
  opened = false;
  calendarOpened = false;
  calendarMinimized = false;
  filterValue = '';
  currentDate = new Date();
  vendorId: number = 0;
  rtoType: string = '';
  month: any = 0;
  year: number = 0;
  employeeId: string = '';
  resetContractorData: RTOdrill[] = [];
  contractorData: RTOdrill[] = [];
  rtodrill = RTODrill;
  componentName: string = PersonaType.RtoContractorList;
  selectedRowData: {
    employeeId: string;
    employeeName: string;
    jobTitle: string;
    location: string | null;
    vendorId: number | null;
  } = {
    employeeId: '',
    employeeName: '',
    jobTitle: '',
    location: null,
    vendorId: null,
  };

  readonly imageSrc = RTODrillData.STATIC_CONFIG.IMAGE_PATHS.NO_DATA;
  readonly errorMsg = RTODrillData.MESSAGES.ERROR.NO_DATA_AVAILABLE;
  activeFilters = [...RTODrillData.STATIC_CONFIG.DEFAULT_FILTERS];
  readonly modalHeaderActions = [...RTODrillData.STATIC_CONFIG.MODAL_ACTIONS];
  readonly calendarNavButtons = RTODrillData.UTILS.getCalendarNavButtons();
  rto = RtoTypeEnum;

  constructor(
    private readonly searchService: SearchService,
    private vendorListRtoDrillService: VendorListRtoDrillService,
    private toastService: ToastrService,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.generateCalendar();
    this.vendorId = Number(this.route.snapshot.queryParams['vendorId']) || 0;
    this.rtoType = this.route.snapshot.queryParams['type'] || '';
    this.month = Number(this.route.snapshot.queryParams['month']) || 0;
    this.year = Number(this.route.snapshot.queryParams['year']) || 0;
    this.employeeId = this.route.snapshot.queryParams['employeeId'] || '';
    if (this.vendorId && this.month && this.year) {
      this.getVendorListRtoDrill(
        this.employeeId,
        this.vendorId,
        this.month,
        this.year,
      );
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  clearInput(): void {
    this.contractorData = this.resetContractorData;
    this.filterValue = '';
  }

  openCalendar(contractor: any): void {
    this.selectedContractor = contractor;
    this.selectedRowData = {
      employeeId: contractor.employeeId,
      employeeName: contractor.employeeName,
      jobTitle: contractor.jobTitle,
      location: contractor.location,
      vendorId: contractor.vendorId,
    };
    this.calendarOpened = true;
    this.calendarMinimized = false;
    this.generateCalendar();
  }

  closeCalendar(): void {
    this.calendarOpened = false;
    this.calendarMinimized = false;
    this.selectedContractor = null;
  }

  minimizeCalendar(): void {
    this.calendarMinimized = !this.calendarMinimized;
  }

  goToToday(): void {
    this.currentDate = new Date();
    this.generateCalendar();
  }

  generateCalendar(): void {
    this.calendarDays = this.generateCalendarDays(
      this.currentDate,
      this.selectedContractor,
    );
  }

  private generateCalendarDays(
    currentDate: Date,
    selectedContractor: any,
  ): CalendarDay[] {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const calendarDays: CalendarDay[] = [];

    for (let i = 0; i < RTODrillData.CALENDAR.DAYS_IN_GRID; i++) {
      const currentDay = new Date(startDate);
      currentDay.setDate(startDate.getDate() + i);

      const dayData: CalendarDay = {
        day: currentDay.getDate(),
        fullDate: currentDay,
        isCurrentMonth: currentDay.getMonth() === month,
        isToday: this.isToday(currentDay),
        isWeekend: this.isWeekend(currentDay),
        attendanceType: this.getAttendanceType(currentDay, selectedContractor),
        tooltip: this.generateTooltip(currentDay, selectedContractor),
      };

      calendarDays.push(dayData);
    }

    return calendarDays;
  }

  private isToday(date: Date): boolean {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  }

  private isWeekend(date: Date): boolean {
    const day = date.getDay();
    return day === 0 || day === 6;
  }

  private getAttendanceType(date: Date, contractor: any): string | null {
    if (!contractor) return null;

    const day = date.getDay();
    const dateNum = date.getDate();

    if (this.isWeekend(date)) return null;

    if (contractor.rto === RTODrillData.CALENDAR.RTO_POLICIES.THREE_DAYS) {
      if (day === 1 || day === 3 || day === 5) {
        return RTODrillData.CALENDAR.ATTENDANCE_TYPES.OFFICE;
      }
      if (day === 2 || day === 4) {
        return RTODrillData.CALENDAR.ATTENDANCE_TYPES.REMOTE;
      }
    } else if (contractor.rto === RTODrillData.CALENDAR.RTO_POLICIES.TEN_DAYS) {
      if (dateNum % 3 === 0) {
        return RTODrillData.CALENDAR.ATTENDANCE_TYPES.REMOTE;
      }
      if (dateNum % 7 === 0) {
        return RTODrillData.CALENDAR.ATTENDANCE_TYPES.ABSENT;
      }
      return RTODrillData.CALENDAR.ATTENDANCE_TYPES.OFFICE;
    }

    if (dateNum % 4 === 0) return RTODrillData.CALENDAR.ATTENDANCE_TYPES.ABSENT;
    if (dateNum % 3 === 0) return RTODrillData.CALENDAR.ATTENDANCE_TYPES.REMOTE;
    return RTODrillData.CALENDAR.ATTENDANCE_TYPES.OFFICE;
  }

  private generateTooltip(date: Date, contractor: any): string {
    const attendanceType = this.getAttendanceType(date, contractor);
    const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
    const dateStr = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });

    if (this.isWeekend(date)) {
      return RTODrillData.UTILS.formatTooltip.weekend(dayName, dateStr);
    }

    switch (attendanceType) {
      case RTODrillData.CALENDAR.ATTENDANCE_TYPES.OFFICE:
        return RTODrillData.UTILS.formatTooltip.office(
          dayName,
          dateStr,
          RTODrillData.TIME.OFFICE_IN,
          RTODrillData.TIME.OFFICE_OUT,
        );
      case RTODrillData.CALENDAR.ATTENDANCE_TYPES.REMOTE:
        return RTODrillData.UTILS.formatTooltip.remote(dayName, dateStr);
      case RTODrillData.CALENDAR.ATTENDANCE_TYPES.ABSENT:
        return RTODrillData.UTILS.formatTooltip.absent(dayName, dateStr);
      default:
        return RTODrillData.UTILS.formatTooltip.default(dayName, dateStr);
    }
  }

  handleFilterApply(filters: any): void {
    this.contractorData = filters;
    this.opened = false;
  }

  handleFilterReset(): void {
    this.contractorData = this.resetContractorData;
  }

  searchContractor(event: KeyboardEvent) {
    this.filterValue = this.searchService.getFilteredValue(
      event as KeyboardEvent,
    );
    this.contractorData = this.resetContractorData?.length
      ? this.searchService.getSearchedData(
          this.resetContractorData,
          this.filterValue?.toLowerCase(),
        )
      : [];
  }

  rtoTypeBadge() {
    return this.rtoType.toUpperCase() === this.rto.Regular.toUpperCase()
      ? this.rto.Regular
      : this.rtoType.toUpperCase() === this.rto.Hybrid.toUpperCase()
        ? this.rto.Hybrid
        : this.rtoType.toUpperCase() === this.rto.Remote.toUpperCase()
          ? this.rto.Remote
          : this.rto.PartialHybrid;
  }

  getVendorListRtoDrill(
    employeeId: string,
    vendorId: number,
    month: number,
    year: number,
  ) {
    this.vendorListRtoDrillService
      .getVendorListRtoDrill(employeeId, vendorId, month, year)
      .subscribe(
        (response: RTOdrillResponse) => {
          let data = response.data ?? [];
          if (this.rtoType) {
            data = data.filter(
              (item) =>
                item.rtoType?.toLowerCase() === this.rtoType.toLowerCase(),
            );
          }
          this.contractorData = data;
          this.resetContractorData = data;
        },
        (error) => {
          this.toastService.error(ErrorMessages.NoDataAvailable);
        },
      );
  }

  navigationTOVendor(type: string): void {
    const queryParams = {
      vendorId: this.vendorId,
      type: this.rtoType,
      month: this.month,
      year: this.year,
      employeeId: this.employeeId,
      // Add any other relevant params here
    };
    if (type === 'vm') {
      this.router.navigate([this.rtodrill.vendor], { queryParams });
    } else if (type === 'vl') {
      this.router.navigate([this.rtodrill.list], { queryParams });
    }
  }
}
